/**
 * ManeuverSummaryCard - Retro 80s themed mission control display
 *
 * Displays collision avoidance maneuver summary with:
 * - Delta-V Vector (VNB frame) with components and magnitude
 * - Maneuver time
 * - Miss distance improvement (before → after)
 * - TCA shift
 *
 * Theme: Retro 80s mission control aesthetic
 * - Dark background with amber/orange/cyan accents
 * - Monospace fonts
 * - Subtle glow effects on key metrics
 */

interface ManeuverSummaryCardProps {
  deltaV: {
    v_m_s: number
    n_m_s: number
    b_m_s: number
    magnitude_m_s: number
  }
  maneuverTime: string
  missDistanceBefore: number | null
  missDistanceAfter: number
  tcaShift: number
  primaryNoradId: number
  secondaryNoradId: number
}

/**
 * Format a number with specified decimal places and optional sign
 */
const formatNumber = (value: number, decimals: number = 4, showSign: boolean = true): string => {
  const formatted = Math.abs(value).toFixed(decimals)
  if (showSign) {
    return value >= 0 ? `+${formatted}` : `-${formatted}`
  }
  return formatted
}

/**
 * Format datetime string to mission control style
 */
const formatMissionTime = (isoString: string): string => {
  try {
    const date = new Date(isoString)
    return date
      .toISOString()
      .replace('T', ' ')
      .replace('Z', ' UTC')
      .slice(0, 23) + ' UTC'
  } catch {
    return isoString
  }
}

/**
 * Format TCA shift to human readable format
 */
const formatTcaShift = (seconds: number): string => {
  const absSeconds = Math.abs(seconds)
  if (absSeconds < 60) {
    return `${formatNumber(seconds, 1)} sec`
  }
  const minutes = seconds / 60
  return `${formatNumber(minutes, 2)} min`
}

/**
 * Calculate improvement percentage. Returns null when the baseline is unknown
 * — fabricating an improvement number on missing safety data would mislead the
 * operator. Real CDM miss_distance must come from the backend.
 */
const calculateImprovement = (before: number | null, after: number): number | null => {
  if (before === null || before === 0) return null
  return ((after - before) / before) * 100
}

function ManeuverSummaryCard({
  deltaV,
  maneuverTime,
  missDistanceBefore,
  missDistanceAfter,
  tcaShift,
  primaryNoradId,
  secondaryNoradId,
}: ManeuverSummaryCardProps) {
  const improvement = calculateImprovement(missDistanceBefore, missDistanceAfter)
  const isImproved = missDistanceBefore !== null && missDistanceAfter > missDistanceBefore

  return (
    <div
      className="font-mono text-sm rounded-lg border p-4"
      style={{
        backgroundColor: '#0d0d0d',
        borderColor: '#333333',
        boxShadow: '0 0 20px rgba(255, 204, 102, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between border-b pb-3 mb-4"
        style={{ borderColor: '#333333' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: '#66cccc' }}
          />
          <span
            className="text-xs font-bold tracking-widest uppercase"
            style={{ color: '#ffcc66' }}
          >
            Maneuver Summary
          </span>
        </div>
        <div className="text-xs" style={{ color: '#666666' }}>
          {primaryNoradId} vs {secondaryNoradId}
        </div>
      </div>

      {/* Delta-V Section */}
      <div className="mb-4">
        <div
          className="text-xs font-bold tracking-wide mb-2 uppercase"
          style={{ color: '#ff8c42' }}
        >
          Delta-V Vector (VNB Frame)
        </div>
        <div
          className="grid grid-cols-2 gap-2 p-3 rounded"
          style={{ backgroundColor: '#1a1a1a', border: '1px solid #333333' }}
        >
          {/* V Component */}
          <div className="flex justify-between items-center">
            <span style={{ color: '#888888' }}>V (prograde)</span>
            <span
              className="tabular-nums"
              style={{
                color: '#66cccc',
                textShadow: '0 0 8px rgba(102, 204, 204, 0.5)',
              }}
            >
              {formatNumber(deltaV.v_m_s)} m/s
            </span>
          </div>

          {/* N Component */}
          <div className="flex justify-between items-center">
            <span style={{ color: '#888888' }}>N (normal)</span>
            <span
              className="tabular-nums"
              style={{
                color: '#66cccc',
                textShadow: '0 0 8px rgba(102, 204, 204, 0.5)',
              }}
            >
              {formatNumber(deltaV.n_m_s)} m/s
            </span>
          </div>

          {/* B Component */}
          <div className="flex justify-between items-center">
            <span style={{ color: '#888888' }}>B (binormal)</span>
            <span
              className="tabular-nums"
              style={{
                color: '#66cccc',
                textShadow: '0 0 8px rgba(102, 204, 204, 0.5)',
              }}
            >
              {formatNumber(deltaV.b_m_s)} m/s
            </span>
          </div>

          {/* Magnitude */}
          <div className="flex justify-between items-center">
            <span style={{ color: '#ffcc66', fontWeight: 'bold' }}>|ΔV|</span>
            <span
              className="tabular-nums font-bold"
              style={{
                color: '#ffcc66',
                textShadow: '0 0 10px rgba(255, 204, 102, 0.6)',
              }}
            >
              {formatNumber(deltaV.magnitude_m_s, 4, false)} m/s
            </span>
          </div>
        </div>
      </div>

      {/* Maneuver Time */}
      <div className="mb-4">
        <div
          className="text-xs font-bold tracking-wide mb-1 uppercase"
          style={{ color: '#ff8c42' }}
        >
          Maneuver Epoch
        </div>
        <div
          className="p-2 rounded tabular-nums"
          style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333333',
            color: '#66cccc',
            letterSpacing: '0.05em',
          }}
        >
          {formatMissionTime(maneuverTime)}
        </div>
      </div>

      {/* Miss Distance Section */}
      <div className="mb-4">
        <div
          className="text-xs font-bold tracking-wide mb-2 uppercase"
          style={{ color: '#ff8c42' }}
        >
          Miss Distance
        </div>
        <div
          className="p-3 rounded"
          style={{ backgroundColor: '#1a1a1a', border: '1px solid #333333' }}
        >
          <div className="flex items-center justify-between gap-4">
            {/* Before */}
            <div className="text-center">
              <div className="text-xs mb-1" style={{ color: '#666666' }}>
                BEFORE
              </div>
              <div
                className="text-lg tabular-nums"
                style={{ color: missDistanceBefore !== null ? '#ff8c42' : '#666666' }}
              >
                {missDistanceBefore !== null ? missDistanceBefore.toFixed(0) : '—'}
              </div>
              <div className="text-xs" style={{ color: '#666666' }}>
                {missDistanceBefore !== null ? 'm' : 'unknown'}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex-1 flex items-center justify-center">
              <div
                className="flex items-center gap-1"
                style={{ color: '#333333' }}
              >
                <span>─</span>
                <span>─</span>
                <span style={{ color: isImproved ? '#66cccc' : '#ff6666' }}>▶</span>
                <span>─</span>
                <span>─</span>
              </div>
            </div>

            {/* After */}
            <div className="text-center">
              <div className="text-xs mb-1" style={{ color: '#666666' }}>
                AFTER
              </div>
              <div
                className="text-lg tabular-nums font-bold"
                style={{
                  color: isImproved ? '#66cccc' : '#ff6666',
                  textShadow: isImproved
                    ? '0 0 12px rgba(102, 204, 204, 0.6)'
                    : '0 0 12px rgba(255, 102, 102, 0.6)',
                }}
              >
                {missDistanceAfter.toFixed(0)}
              </div>
              <div className="text-xs" style={{ color: '#666666' }}>
                m
              </div>
            </div>

            {/* Improvement */}
            <div
              className="text-center px-3 py-2 rounded"
              style={{
                backgroundColor: isImproved ? 'rgba(102, 204, 204, 0.1)' : 'rgba(255, 102, 102, 0.1)',
                border: `1px solid ${isImproved ? '#66cccc' : '#ff6666'}`,
              }}
            >
              <div
                className="text-lg tabular-nums font-bold"
                style={{
                  color: isImproved ? '#66cccc' : '#ff6666',
                  textShadow: isImproved
                    ? '0 0 10px rgba(102, 204, 204, 0.5)'
                    : '0 0 10px rgba(255, 102, 102, 0.5)',
                }}
              >
                {improvement !== null
                  ? `${improvement >= 0 ? '+' : ''}${improvement.toFixed(1)}%`
                  : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TCA Shift */}
      <div>
        <div
          className="text-xs font-bold tracking-wide mb-1 uppercase"
          style={{ color: '#ff8c42' }}
        >
          TCA Shift
        </div>
        <div
          className="flex items-center justify-between p-2 rounded"
          style={{ backgroundColor: '#1a1a1a', border: '1px solid #333333' }}
        >
          <span style={{ color: '#888888' }}>Time of Closest Approach</span>
          <span
            className="tabular-nums"
            style={{
              color: '#ff99aa',
              textShadow: '0 0 8px rgba(255, 153, 170, 0.5)',
            }}
          >
            {formatTcaShift(tcaShift)}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        className="mt-4 pt-3 border-t flex justify-between items-center"
        style={{ borderColor: '#333333' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: isImproved ? '#66cccc' : '#ff6666' }}
          />
          <span className="text-xs uppercase" style={{ color: '#666666' }}>
            {isImproved ? 'Collision Risk Reduced' : 'Review Required'}
          </span>
        </div>
        <div className="text-xs" style={{ color: '#444444' }}>
          VNB Reference Frame
        </div>
      </div>
    </div>
  )
}

export default ManeuverSummaryCard
